import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Paper, Box, Typography, Divider, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Button } from '@mui/material'
import { PROTOCOL_LABELS } from '../../ocpp-core/protocol'

const TO_CSMS = 'toCsms'
const FROM_CSMS = 'fromCsms'

// Send any message of the connected OCPP version, schema-checked before it goes out:
//  - Charge point -> CSMS: every message the charge point may send.
//  - CSMS -> charge point (local test): every message a CSMS may send, handled by this charge
//    point as if the CSMS had sent it (its answer is logged; follow-up notifications still go to
//    the CSMS). A real CSMS sends these through its own API, e.g. ocpp-server
//    POST /api/ocpp/charge-points/:id/command.
const MessageSender = ({ simulator, onError }) => {
  const [direction, setDirection] = useState(TO_CSMS)
  const actions = direction === TO_CSMS ? simulator.chargePointActions : simulator.csmsActions
  const preferred = direction === TO_CSMS ? 'DataTransfer' : (simulator.protocol === 'ocpp1.6' ? 'GetDiagnostics' : 'GetLog')
  const [action, setAction] = useState(actions.includes(preferred) ? preferred : actions[0] || '')
  const [payload, setPayload] = useState('')
  const [parseError, setParseError] = useState('')

  useEffect(() => {
    if (!action) return
    const example = direction === TO_CSMS ? simulator.examplePayload(action) : simulator.exampleCsmsPayload(action)
    setPayload(JSON.stringify(example, null, 2))
    setParseError('')
  }, [action, direction, simulator])

  const changeDirection = (next) => {
    const list = next === TO_CSMS ? simulator.chargePointActions : simulator.csmsActions
    const nextPreferred = next === TO_CSMS ? 'DataTransfer' : (simulator.protocol === 'ocpp1.6' ? 'GetDiagnostics' : 'GetLog')
    setDirection(next)
    setAction(list.includes(nextPreferred) ? nextPreferred : list[0] || '')
  }

  const send = () => {
    let params
    try {
      params = JSON.parse(payload)
    } catch (error) {
      setParseError(`Invalid JSON: ${error.message}`)
      return
    }
    setParseError('')
    const sending = direction === TO_CSMS ? simulator.sendRaw(action, params) : simulator.injectCsmsCall(action, params)
    sending.catch((error) => onError(`${action}: ${error.code || 'Error'} ${error.message}`))
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant='h6' color='primary'>SEND MESSAGE ({PROTOCOL_LABELS[simulator.protocol]})</Typography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Direction</InputLabel>
            <Select size='small' label='Direction' value={direction} onChange={(e) => changeDirection(e.target.value)}>
              <MenuItem value={TO_CSMS}>Charge point → CSMS</MenuItem>
              <MenuItem value={FROM_CSMS}>CSMS → charge point (local test)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Message</InputLabel>
            <Select size='small' label='Message' value={action} onChange={(e) => setAction(e.target.value)}>
              {actions.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </Select>
          </FormControl>
          <Box mt={2}>
            <Button fullWidth variant='contained' onClick={send} disabled={!action}>Send</Button>
          </Box>
        </Grid>
        <Grid item xs={9}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={14}
            size='small'
            label='Payload (JSON)'
            value={payload}
            error={Boolean(parseError)}
            helperText={parseError || (direction === FROM_CSMS ? 'Handled by this charge point as if the CSMS had sent it; the answer is in the log.' : ' ')}
            onChange={(e) => setPayload(e.target.value)}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}

MessageSender.propTypes = {
  simulator: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
}

export default MessageSender
